import { AxiosResponse } from "axios";
import api from "../utils/apiClient"; // Use centralized API client

export interface AsyncQuestionRequest {
  question: string;
  debug?: boolean;
  thread_id?: string;
}

export interface AsyncQuestionResponse {
  request_id: string;
  status: "pending" | "processing" | "completed" | "failed";
  message?: string;
}

export interface RequestStatus {
  request_id: string;
  status: "pending" | "processing" | "completed" | "failed";
  result?: any;
  error?: string;
  progress?: number;
}

export interface PollingOptions {
  maxAttempts?: number;
  intervalMs?: number;
  timeoutMs?: number;
  onProgress?: (progress: number) => void;
  onStatusChange?: (status: string) => void;
}

export const pollingService = {
  // Submit async question
  async submitAsyncQuestion(
    request: AsyncQuestionRequest
  ): Promise<AsyncQuestionResponse> {
    try {
      const response: AxiosResponse<AsyncQuestionResponse> = await api.post(
        "/chat/ask-async",
        request
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail || "Failed to submit question"
      );
    }
  },

  // Poll for request status
  async pollRequestStatus(requestId: string): Promise<RequestStatus> {
    try {
      const response: AxiosResponse<RequestStatus> = await api.get(
        `/chat/status/${requestId}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail || "Failed to get request status"
      );
    }
  },

  // Poll with retry logic
  async pollWithRetry(
    requestId: string,
    options: PollingOptions = {}
  ): Promise<RequestStatus> {
    const {
      maxAttempts = 60,
      intervalMs = 1000,
      onProgress,
      onStatusChange,
    } = options;

    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const status = await this.pollRequestStatus(requestId);

        // Call status change callback if provided
        if (onStatusChange) {
          onStatusChange(status.status);
        }

        // Call progress callback if provided
        if (onProgress && status.progress !== undefined) {
          onProgress(status.progress);
        }

        if (status.status === "completed" || status.status === "failed") {
          return status;
        }

        // Wait before next poll
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        attempts++;
      } catch (error) {
        attempts++;
        if (attempts >= maxAttempts) {
          throw error;
        }
        // Wait before retry
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
    }

    throw new Error("Polling timeout - request did not complete in time");
  },

  // Ask question with polling - Complete workflow
  async askQuestionWithPolling(
    question: string,
    threadId: string = "default",
    debug: boolean = false,
    options: PollingOptions = {}
  ): Promise<any> {
    try {
      // Submit the async question
      const submitResponse = await this.submitAsyncQuestion({
        question,
        thread_id: threadId,
        debug,
      });

      // Poll for completion with options
      const finalStatus = await this.pollWithRetry(
        submitResponse.request_id,
        options
      );

      if (finalStatus.status === "completed") {
        return { result: finalStatus.result };
      } else if (finalStatus.status === "failed") {
        throw new Error(finalStatus.error || "Request failed");
      } else {
        throw new Error("Request did not complete successfully");
      }
    } catch (error: any) {
      throw new Error(error.message || "Failed to ask question with polling");
    }
  },
};
